import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components'
import { ALL_CARRIERS, errorFields, errorKey, getErrorCode, payloadFor } from './recipientsConfig'

const T_PREFIX = 'dispatch_recipients'

export const RecipientFormModal = ({
  open,
  setOpen,
  recipient = null,
  group = ALL_CARRIERS,
  carriers = [],
  onSubmit,
  isLoading = false,
  error = null,
  onClearError = () => {},
}) => {
  const { i18n } = useTranslation()
  const nameRef = useRef(null)
  const [carrierName, setCarrierName] = useState(ALL_CARRIERS)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) return
    setCarrierName(group || ALL_CARRIERS)
    setName(recipient?.name || '')
    setEmail(recipient?.email || '')
    setSubmitted(false)
  }, [open, recipient, group])

  // A carrier no longer among the warehouse's still shows, so editing its recipient keeps it.
  const carrierOptions = useMemo(
    () => (carrierName === ALL_CARRIERS || carriers.includes(carrierName) ? carriers : [...carriers, carrierName]),
    [carriers, carrierName],
  )
  // Whether the email is valid is the platform's call: the form only asks for both fields, and shows
  // each field the platform refused under it.
  const refused = errorFields(error)
  const fieldError = (field, value) => {
    if (submitted && value.trim() === '') return i18n.t(`${T_PREFIX}.errors.required`)
    if (!refused.includes(field)) return null
    return field === 'email' && getErrorCode(error) === 'RECIPIENT_DUPLICATED'
      ? i18n.t(`${T_PREFIX}.errors.RECIPIENT_DUPLICATED`)
      : i18n.t(`${T_PREFIX}.errors.fields.${field}`)
  }
  const nameError = fieldError('name', name)
  const emailError = fieldError('email', email)
  const carrierError = refused.includes('carrier_name') ? i18n.t(`${T_PREFIX}.errors.fields.carrier_name`) : null
  // An error that names no field (no permission, removed meanwhile, unexpected) is said once, for the form.
  const formError = error && refused.length === 0 ? i18n.t(`${T_PREFIX}.errors.${errorKey(error)}`) : null

  const change = setter => e => {
    setter(e.target.value)
    if (error) onClearError()
  }

  const handleSubmit = e => {
    e.preventDefault()
    setSubmitted(true)
    if (name.trim() && email.trim()) onSubmit(payloadFor({ carrierName, name, email }))
  }

  const inputClass = invalid =>
    `shadow-sm block w-full sm:text-sm rounded-md ${invalid ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`

  const handleClose = () => !isLoading && setOpen(false)

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={nameRef} onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {i18n.t(recipient ? `${T_PREFIX}.form.edit_title` : `${T_PREFIX}.form.create_title`)}
                </Dialog.Title>
                <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose} aria-label={i18n.t(`${T_PREFIX}.form.cancel`)}>
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="recipient-carrier" className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.form.carrier`)}
                  </label>
                  <select
                    id="recipient-carrier"
                    value={carrierName}
                    onChange={change(setCarrierName)}
                    aria-invalid={!!carrierError}
                    aria-describedby={carrierError ? 'recipient-carrier-error' : undefined}
                    className={inputClass(!!carrierError)}
                  >
                    <option value={ALL_CARRIERS}>{i18n.t(`${T_PREFIX}.all_carriers`)}</option>
                    {carrierOptions.map(carrier => (
                      <option key={carrier} value={carrier}>
                        {carrier}
                      </option>
                    ))}
                  </select>
                  {carrierError && (
                    <p id="recipient-carrier-error" className="mt-1 text-xs text-red-500">
                      {carrierError}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="recipient-name" className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.form.name`)}
                  </label>
                  <input
                    id="recipient-name"
                    ref={nameRef}
                    type="text"
                    maxLength={120}
                    value={name}
                    onChange={change(setName)}
                    placeholder={i18n.t(`${T_PREFIX}.form.name_placeholder`)}
                    aria-invalid={!!nameError}
                    aria-describedby={nameError ? 'recipient-name-error' : undefined}
                    className={inputClass(!!nameError)}
                  />
                  {nameError && (
                    <p id="recipient-name-error" className="mt-1 text-xs text-red-500">
                      {nameError}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="recipient-email" className="block text-sm font-medium text-gray-700 mb-1">
                    {i18n.t(`${T_PREFIX}.form.email`)}
                  </label>
                  <input
                    id="recipient-email"
                    type="email"
                    maxLength={254}
                    value={email}
                    onChange={change(setEmail)}
                    placeholder="nombre@empresa.com"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'recipient-email-error' : undefined}
                    className={inputClass(!!emailError)}
                  />
                  {emailError && (
                    <p id="recipient-email-error" className="mt-1 text-xs text-red-500">
                      {emailError}
                    </p>
                  )}
                </div>

                {formError && (
                  <p role="alert" className="text-sm text-red-600">
                    {formError}
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="secondary" onClick={handleClose} disabled={isLoading} className="px-4">
                    {i18n.t(`${T_PREFIX}.form.cancel`)}
                  </Button>
                  <Button type="primary" buttonType="submit" loading={isLoading} disabled={isLoading} className="px-4">
                    {i18n.t(recipient ? `${T_PREFIX}.form.save` : `${T_PREFIX}.form.create`)}
                  </Button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
